import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { APP_ROUTES } from "../../constants/approutes";
import {
  CheckCircle2,
  ClipboardList,
  Eye,
  FilePlus2,
  LogOut,
  RotateCcw,
  Search,
} from "lucide-react";
import axios from "axios";
import { endOfMonth, format, startOfMonth, parseISO,parse,isValid } from "date-fns";
import { toast } from "react-toastify";
import OurLayouts from "../layout";
import BillingUploadSidebar from "./BillingUploadSidebar";
import DateRangeFilter from "../shared/DateRangeFilter";

const HOSPITAL_API_BASE = process.env.REACT_APP_SURGERY_STATUS;

const formatApiDate = (date) => format(date, "MM/dd/yyyy");
const formatServiceDate = (date = new Date()) => format(date, "yyyy-MM-dd 00:00:00");

const getDefaultFilters = () => {
  const now = new Date();
  return {
    enquiryId: "",
    nameSearch: "",
    fromDate: formatApiDate(startOfMonth(now)),
    toDate: formatApiDate(endOfMonth(now)),
  };
};

const formatEnquiryDateTime = (value) => {
  if (!value) return { date: "—", time: "" };
  const str = String(value);
  const [datePart, timePart] = str.includes("T") ? str.split("T") : str.split(" ");
  return {
    date: datePart || str,
    time: timePart ? timePart.replace(/\.\d+Z?$/, "").slice(0, 8) : "",
  };
};

const getStatusStyle = (status = "") => {
  const value = String(status).toLowerCase();
  const code = Number(status);
  if (value.includes("complete") || code === 2) {
    return {
      label: "Complete",
      dot: "bg-[#2F9D64]",
      className: "border-[#A9E0C1] text-[#2F9D64] bg-[#F0FBF4]",
    };
  }
  if (value.includes("pending") || code === 7 || code === 11) {
    return {
      label: "Pending",
      dot: "bg-[#B8860B]",
      className: "border-[#F5E6B8] text-[#B8860B] bg-[#FFFBF0]",
    };
  }
  return {
    label: status ? String(status) : "—",
    dot: "bg-[#C53232]",
    className: "border-[#EFB3B3] text-[#C53232] bg-[#FFF8F8]",
  };
};

const getInitials = (name) =>
  (name || "?")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const isBillingUploaded = (patient) => Number(patient?.billingStatus) === 1;

// Procedure workflow stages: Pending → Procedure Confirmed → Patient Discharged
const PROCEDURE_STAGE = {
  PENDING: 0,
  CONFIRMED: 1,
  DISCHARGED: 2,
};

const STAGE_META = {
  0: {
    label: "New",
    className: "border-[#F5E6B8] text-[#B8860B] bg-[#FFFBF0]",
    dot: "bg-[#B8860B]",
  },
  1: {
    label: "Procedure Confirmed",
    className: "border-[#BBD8F0] text-[#0A4C8A] bg-[#EFF6FC]",
    dot: "bg-[#0A4C8A]",
  },
  2: {
    label: "Patient Discharged",
    className: "border-[#A9E0C1] text-[#2F9D64] bg-[#F0FBF4]",
    dot: "bg-[#2F9D64]",
  },
};

// Status semantics from getEnquiryList:
//   status === 11        -> New (procedure not yet confirmed)
//   status === 6         -> Procedure Confirmed
//   conciergeStatus === 4-> Patient Discharged
const derivePatientStage = (patient) => {
  if (Number(patient?.conciergeStatus) === 4) {
    return PROCEDURE_STAGE.DISCHARGED;
  }
  if (Number(patient?.status) === 6) {
    return PROCEDURE_STAGE.CONFIRMED;
  }
  return PROCEDURE_STAGE.PENDING;
};

const normalizeServiceDate = (value) => {
  if (!value) return formatServiceDate();
  const parsed = new Date(String(value).replace(/\.0$/, ""));
  if (Number.isNaN(parsed.getTime())) return formatServiceDate();
  return format(parsed, "yyyy-MM-dd 00:00:00");
};

const hospitalizationTypeMap = {
  8: [
    { id: 1, value: "General Surgery" },
    { id: 2, value: "Oncology" },
    { id: 3, value: "Neurology" },
    { id: 4, value: "Orthopedic" },
    { id: 5, value: "Urology" },
    { id: 6, value: "Cardiology" },
    { id: 7, value: "ENT" },
    { id: 8, value: "Gynecology" },
    { id: 10, value: "Opthalmology" },
    { id: 11, value: "General Medicine" },
    { id: 15, value: "Nephrology" },
  ],
  9: [{ id: 12, value: "OPD Others" }],
  10: [{ id: 13, value: "Investigation Others" }],
  11: [{ id: 14, value: "Day Care Others" }],
};

const resolveHospitalizationTypeName = (serviceType, hospitalizationType) => {
  if (hospitalizationType == null) return null;
  const options = hospitalizationTypeMap[Number(serviceType)] || [];
  const match = options.find(
    (option) => String(option.id) === String(hospitalizationType)
  );
  return match ? match.value : null;
};

const SUB_SERVICE_LIST = [
  { value: 1, name: "Emergency" },
  { value: 2, name: "Non-Emergency" },
  { value: 3, name: "Others" },
];

const resolveServiceSubTypeName = (serviceSubTypeId) => {
  if (serviceSubTypeId == null) return null;
  const match = SUB_SERVICE_LIST.find(
    (option) => String(option.value) === String(serviceSubTypeId)
  );
  return match ? match.name : null;
};

const buildBillingServicePayload = (patient, enquiryDetails = null) => {
  const fallbackUserId = localStorage.getItem("userId") || "";
  const fallbackHospitalId = localStorage.getItem("hospitalid") || "";

  const hospitalId =
    fallbackHospitalId;

  const locationId =
    patient.locationId ||
    patient.eclinicLocationId ||
    (hospitalId ? `LOC_${hospitalId}` : "");

  const serviceDate = normalizeServiceDate(
    patient.procedurePlannedDate
  );

  const details = enquiryDetails || {};

  const serviceType = details.serviceType ?? patient.serviceType ?? null;
  const hospitalizationTypeId =
    details.hospitalizationTypeId ?? patient.hospitalizationTypeId ?? null;
  const rawHospitalizationType =
    details.hospitalizationType ?? patient.hospitalizationType ?? null;
  const hospitalizationTypeName =
    resolveHospitalizationTypeName(serviceType, rawHospitalizationType) ??
    resolveHospitalizationTypeName(serviceType, hospitalizationTypeId);

  const serviceSubTypeId =
    details.serviceSubTypeId ?? patient.serviceSubTypeId ?? null;
  const serviceSubTypeName =
    resolveServiceSubTypeName(serviceSubTypeId) ??
    details.serviceSubType ??
    patient.serviceSubType ??
    null;

  return {
    patientId: patient.patientId || "",
    patientName: patient.patientName || "",
    serviceEnquiryId: patient.id,
    createdBy: fallbackUserId,
    modifiedBy: fallbackUserId,
    serviceCode: "CHE_BILLING",
    serviceName: "eClinic billing",
    status: patient.status ?? 1,
    userCreatedBy: fallbackUserId,
    userRoleCode: patient.roleCode || "CHE",
    serviceStatus: 1,
    hospitalId,
    locationId,
    cheCenterId: patient.eclinicId || "",
    cheCenterName: patient.eclinicName || "",
    cheBranchId: patient.eclinicLocationId || "",
    cheBranchName: patient.eclinicLocationName || "",
    procedureDate: serviceDate,
    admissionFromDate: normalizeServiceDate(patient.fromDate || patient.createdDate),
    admissionToDate: normalizeServiceDate(patient.toDate || patient.createdDate),
    billingStatus: patient.billingStatus ?? 0,
    procedureName: details.procedureName ?? patient.procedureName ?? "",
    procedureId: details.procedureId ?? patient.procedureId ?? "",
    serviceType,
    serviceSubType: serviceSubTypeName,
    serviceSubTypeId,
    hospitalizationType: hospitalizationTypeName ?? rawHospitalizationType,
    hospitalizationTypeId,
  };
};

const SkeletonRow = () => (
  <tr className="animate-pulse">
    {[80, 200, 130, 180, 110, 80, 100].map((w, i) => (
      <td key={i} className="px-4 py-4 border-b border-r border-[#EEEEEE]">
        <div className="h-4 bg-[#EDF0F3] rounded-md" style={{ width: w }} />
        {i === 1 && <div className="h-3 bg-[#F4F6F8] rounded-md mt-2 w-24" />}
      </td>
    ))}
  </tr>
);

const CampPatient = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const campCode = location.state?.campCode || "";

  const [filters, setFilters] = useState(getDefaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(getDefaultFilters);
  const [campPatients, setCampPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [billingSidebarOpen, setBillingSidebarOpen] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingServiceRecord, setBillingServiceRecord] = useState(null);
  const [billingPatientSummary, setBillingPatientSummary] = useState(null);
  const [creatingBillingForId, setCreatingBillingForId] = useState(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [selectedCampCode, setSelectedCampCode] = useState("");
  const debounceRef = useRef(null);
  const [campDetails, setCampDetails] = useState([]);
    const [hospitalInfo, setHospitalInfo] = useState(null);
    const getDefaultDateRange = () => {
  const now = new Date();
  return {
    fromDate: format(startOfMonth(now), "MM/dd/yyyy"),
    toDate: format(endOfMonth(now), "MM/dd/yyyy"),
  };
};
  const [appliedRange, setAppliedRange] = useState(getDefaultDateRange);
  const getPatientStage = (patient) => derivePatientStage(patient);
  const hospitalCode = localStorage.getItem("hospitalid") || "";
  const fetchCampPatients = async () => {
    if (!HOSPITAL_API_BASE) {
      toast.error("Hospital API URL is not configured.");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const preferredHospitalAndClinics =
        localStorage.getItem("hospitalid") || "";
      const payload = {
        tab: 1,
        pageNumber: 1,
        campCode: selectedCampCode,
      };

      if (appliedFilters.enquiryId.trim()) {
        payload.id = appliedFilters.enquiryId.trim();
      }
      if (appliedFilters.nameSearch.trim()) {
        payload.nameSearch = appliedFilters.nameSearch.trim();
      }

      const { data } = await axios.post(
        `${HOSPITAL_API_BASE}enquiry/getEnquiryList`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
           
          },
        }
      );

      setCampPatients(Array.isArray(data) ? data : data?.successList || []);
    } catch (error) {
      console.error("Failed to fetch camp patients:", error);
      toast.error(
        error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          "Failed to load camp patient details."
      );
      setCampPatients([]);
    } finally {
      setLoading(false);
    }
  };
const toQueryDateParam = (value) => {
  if (!value) return "";
  const slashParsed = parse(value, "MM/dd/yyyy", new Date());
  if (isValid(slashParsed)) return format(slashParsed, "yyyy-MM-dd");
  const isoParsed = parseISO(String(value));
  if (isValid(isoParsed)) return format(isoParsed, "yyyy-MM-dd");
  return value;
};

  useEffect(() => {
      const loadCampDetails = async () => {
      setLoading(true);
      try {
     
          const token = localStorage.getItem("auth_token");
          const code = hospitalCode ;
          const params = new URLSearchParams({
            fromDate: toQueryDateParam(appliedRange.fromDate),
            toDate: toQueryDateParam(appliedRange.toDate),
          });
          const API_BASE = process.env.REACT_APP_NEW_DOCTOR_URL || "";
          const base = API_BASE.replace(/\/$/, "");
          const url = `${base}/api/v1/hospital/${encodeURIComponent(code)}/camp-details?${params}`;
          const res = await fetch(url, {
            method: "GET",
            headers: {
              Accept: "application/json",
            
            },
          });
         
          const json = await res.json();
          console.log(json?.hospitalDetails?.campDetails, "cfgvhbjknmlhbk");
          const details = json?.hospitalDetails || json;
          setHospitalInfo(details || null);
          const camps = Array.isArray(details?.campDetails) ? details.campDetails : [];
          setCampDetails(camps);
          if (camps.length > 0) {
            setSelectedCampCode(camps[0].campCode);
          }
          else{
            setSelectedCampCode("");
          }
        
      } catch (error) {
     
        setHospitalInfo();
        setCampDetails( []);
      } finally {
        setLoading(false);
      }
    };

    loadCampDetails();
  }, [appliedRange.fromDate, appliedRange.toDate]);
  useEffect(() => {
    fetchCampPatients();
  }, [selectedCampCode,appliedFilters]);
  console.log(selectedCampCode,"selectedCampCode")
  const handleTextFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setAppliedFilters((prev) => ({ ...prev, [field]: value }));
    }, 600);
  };

  const handleDateChange = ({ fromDate, toDate }) => {
    setFilters((prev) => ({ ...prev, fromDate, toDate }));
    setAppliedFilters((prev) => ({ ...prev, fromDate, toDate }));
    setAppliedRange({ fromDate, toDate });
  };

  const handleReset = () => {
    clearTimeout(debounceRef.current);
    const defaults = getDefaultFilters();
    setFilters(defaults);
    setAppliedFilters(defaults);
  };


  const closeBillingSidebar = () => {
    setBillingSidebarOpen(false);
    setBillingServiceRecord(null);
    setBillingPatientSummary(null);
    setBillingLoading(false);
    setCreatingBillingForId(null);
  };

  const handleBillingApprovalSuccess = (successObject) => {
    const enquiryId = successObject?.serviceEnquiryId;
    if (enquiryId != null) {
      setCampPatients((prev) =>
        prev.map((patient) => {
          const patientEnquiryId = patient.id || patient.enquiryId;
          if (String(patientEnquiryId) === String(enquiryId)) {
            return { ...patient, billingStatus: successObject.billingStatus ?? 1 };
          }
          return patient;
        })
      );
    }
    closeBillingSidebar();
  };

  const handleCreateBillingService = async (patient) => {
    const enquiryId = patient?.id || patient?.enquiryId;
    if (!enquiryId) {
      toast.error("Enquiry ID not found.");
      return;
    }
    if (!patient?.patientId && !patient?.patientCode) {
      toast.error("Patient ID not found.");
      return;
    }

    setCreatingBillingForId(enquiryId);
    setBillingSidebarOpen(true);
    setBillingLoading(true);
    setBillingServiceRecord(null);

    let enquiryDetails = null;
    try {
      const token = localStorage.getItem("auth_token");
      const { data } = await axios.get(
        `${HOSPITAL_API_BASE}enquiry/getEnquiryById`,
        {
          params: { enquiryId },
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
      enquiryDetails = data?.successObject || null;
    } catch (error) {
      console.error("Failed to fetch enquiry details for billing:", error);
    }

    const payload = buildBillingServicePayload(patient, enquiryDetails);
    setBillingPatientSummary({
      patientName: payload.patientName,
      serviceEnquiryId: payload.serviceEnquiryId,
      patientContactNumber: patient.patientContactNumber,
      patientEmail: patient.patientEmail,
      hospitalName: patient.hospitalName,
      createdDate: patient.createdDate,
      modifiedBy: payload.hospitalId || localStorage.getItem("hospitalid") || "",
      userRoleCode:  "CE",
      procedureName: payload.procedureName,
    });


    try {
      const token = localStorage.getItem("auth_token");
      const { data } = await axios.post(
        `${HOSPITAL_API_BASE}surgery/saveFromHospitalServiceDetails`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (data?.responseCode && data.responseCode !== "200") {
        throw new Error(data?.responseMessage || "Failed to create billing service.");
      }

      const savedRecord = data?.successObject;
      if (!savedRecord?.id) {
        throw new Error("Billing service created but service ID was not returned.");
      }
      setBillingServiceRecord(savedRecord);

    } catch (error) {
      console.error("Failed to create billing service:", error);
      toast.error(
        error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          "Failed to create billing service."
      );
      setBillingSidebarOpen(false);
      setBillingPatientSummary(null);
    } finally {
      setBillingLoading(false);
      setCreatingBillingForId(null);
    }
  };

  // userCode is the hospital code (saved at login under "hospitalid").
  const resolveUserCode = () => localStorage.getItem("hospitalid") || "";

  // Procedure Confirmed -> updateServiceDetails (status: 6)
  const handleConfirmProcedure = async (patient) => {
    const id = patient?.id || patient?.enquiryId;
    if (!id) {
      toast.error("Enquiry ID not found.");
      return;
    }
    setStatusUpdatingId(id);
    try {
      const token = localStorage.getItem("auth_token");
      await axios.put(
        `${HOSPITAL_API_BASE}enquiry/updateServiceDetails/${id}`,
        {
          id,
          userCode: resolveUserCode(),
          status: 6,
        },
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
      toast.success("Procedure confirmed.");
      await fetchCampPatients();
    } catch (error) {
      console.error("Failed to confirm procedure:", error);
      toast.error(
        error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          "Failed to confirm procedure."
      );
    } finally {
      setStatusUpdatingId(null);
    }
  };

  // Patient Discharge -> updateConsentDetails (conciergeStatus: 4)
  const handlePatientDischarge = async (patient) => {
    const id = patient?.id || patient?.enquiryId;
    if (!id) {
      toast.error("Enquiry ID not found.");
      return;
    }
    setStatusUpdatingId(id);
    try {
      const token = localStorage.getItem("auth_token");
      await axios.put(
        `${HOSPITAL_API_BASE}enquiry/updateConsentDetails/${id}`,
        {
          id,
          userCode: resolveUserCode(),
          conciergeStatus: 4,
        },
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
      toast.success("Patient discharged.");
      await fetchCampPatients();
    } catch (error) {
      console.error("Failed to discharge patient:", error);
      toast.error(
        error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          "Failed to discharge patient."
      );
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const columnCount = 7;

  return (
    <OurLayouts>
      <div className="w-full px-2 sm:px-4 py-5 max-w-[1600px] mx-auto">

        {/* ── Header ── */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="bg-gradient-to-br from-[#0A4C8A] to-[#00B58F] p-3 rounded-2xl shadow-lg shrink-0">
              <ClipboardList size={26} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-[#0B4D76] tracking-tight leading-tight">
                Surgery Enquiry Details
              </h1>
            </div>
          </div>
          {/* {!loading && campPatients.length > 0 && (
            <span className="hidden sm:inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#EAF3FB] border border-[#C5DDEF] text-[#0B4D76] text-[13px] font-semibold">
              {campPatients.length} Records
            </span>
          )} */}
        </div>

        {/* ── Filter Card ── */}
        {/* <div className="bg-white rounded-2xl border border-[#F0F0F0] shadow-sm px-5 py-4 mb-5"> */}
          <div className="flex flex-wrap gap-4 items-center mb-7">

            {/* Camp Name */}
            <div className="rounded-lg p-[0.8px] w-[180px] shrink-0" style={{ background: "linear-gradient(135deg, #004376, #00BA7E)" }}>
              <select
                value={selectedCampCode}
                onChange={(e) => setSelectedCampCode(e.target.value)}
                className="h-10 w-full px-3 pr-8 rounded-[7px] bg-white text-[13px] text-[#004376] font-medium outline-none cursor-pointer"
              >
                <option value="">Camp Name</option>
                {campDetails.map((camp) => (
                  <option key={camp.campCode} value={camp.campCode}>
                    {camp.campName}
                  </option>
                ))}
              </select>
            </div>

            {/* Enquiry ID */}
            <div className="rounded-lg p-[0.8px] w-[160px] shrink-0" style={{ background: "linear-gradient(135deg, #004376, #00BA7E)" }}>
              <input
                type="text"
                placeholder="Enquiry ID"
                value={filters.enquiryId}
                onChange={(e) => handleTextFilterChange("enquiryId", e.target.value)}
                className="h-10 w-full px-3 rounded-[7px] bg-white text-[13px] text-[#374151] placeholder:text-[#9CA3AF] outline-none"
              />
            </div>

            {/* Search Patient */}
            <div className="rounded-lg p-[0.8px] w-[200px] shrink-0" style={{ background: "linear-gradient(135deg, #004376, #00BA7E)" }}>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search Patient"
                  value={filters.nameSearch}
                  onChange={(e) => handleTextFilterChange("nameSearch", e.target.value)}
                  className="h-10 w-full pl-8 pr-3 rounded-[7px] bg-white text-[13px] text-[#374151] placeholder:text-[#9CA3AF] outline-none"
                />
              </div>
            </div>

            {/* Date Range */}
            <div className="rounded-lg p-[0.8px] shrink-0" style={{ background: "linear-gradient(135deg, #004376, #00BA7E)" }}>
              <DateRangeFilter
                label=""
                fromDate={filters.fromDate}
                toDate={filters.toDate}
                onChange={handleDateChange}
                buttonClassName="h-10 px-4 rounded-[7px] bg-white text-[13px] text-[#374151] outline-none flex items-center justify-between gap-3 min-w-[220px]"
              />
            </div>

            {/* Reset */}
            <button
              type="button"
              onClick={handleReset}
              className="h-10 flex items-center gap-2 px-5 rounded-lg border border-[#E0E0E0] text-[#6B6B6B] text-[13px] font-medium bg-white hover:bg-[#F7F7F7] hover:border-[#C0C0C0] transition-colors whitespace-nowrap shrink-0"
            >
              <RotateCcw size={13} />
              Reset
            </button>
          </div>
        {/* </div> */}

        {/* ── Table ── */}
        <div className="rounded-2xl border border-[#E2EBF4] overflow-hidden bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px]">
              <thead>
                <tr className="text-left select-none" style={{ background: "linear-gradient(90deg, #EDF3F8 0%, #E6F4EF 100%)" }}>
                  {[
                    "Enquiry ID",
                    "Patient Name",
                    "Mobile Number",
                    "Email Id",
                    "Enquiry Date",
                    "Status",
                    "Action",
                  ].map((col, i) => (
                    <th
                      key={col}
                      className={`px-5 py-4 text-[11px] font-bold text-[#0B4D76] border-b border-[#D4E4F0] whitespace-nowrap uppercase tracking-widest ${
                        i < 6 ? "border-r border-[#D4E4F0]" : ""
                      } ${i >= 5 ? "text-center" : ""}`}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
                ) : campPatients.length === 0 ? (
                  <tr>
                    <td colSpan={columnCount} className="py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-16 h-16 rounded-full bg-[#F4F8FB] border border-[#E8E8E8] flex items-center justify-center">
                          <ClipboardList size={28} className="text-[#9CA3AF]" />
                        </div>
                        <p className="text-[15px] font-semibold text-[#374151]">No patients found</p>
                        <p className="text-[13px] text-[#9CA3AF]">
                          Try adjusting your search or date range
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  campPatients.map((patient, index) => {
                    const enquiryDate = formatEnquiryDateTime(patient.createdDate);
                    const patientName = patient.patientName || patient.name || "—";
                    const rowId = patient.id || patient.enquiryId;
                    const stage = getPatientStage(patient);
                    const stageMeta = STAGE_META[stage];
                    const isUpdatingStatus = statusUpdatingId === rowId;

                    return (
                      <tr
                        key={patient.id || patient.patientId || index}
                        className={`align-middle transition-colors hover:bg-[#EFF6FF] ${index % 2 === 0 ? "bg-white" : "bg-[#FAFBFD]"}`}
                      >
                        {/* Enquiry ID */}
                        <td className="px-5 py-4 border-b border-r border-[#EEF2F7] whitespace-nowrap">
                          <span className="px-2.5 py-1 rounded-lg bg-[#EDF3FB] text-[#0B4D76] font-bold text-[13px]">
                            {patient.id || "—"}
                          </span>
                        </td>

                        {/* Patient Name */}
                        <td className="px-5 py-4 border-b border-r border-[#EEF2F7]">
                          <p className="text-[14px] font-medium text-[#111827]">{patientName}</p>
                        </td>

                        {/* Mobile */}
                        <td className="px-5 py-4 text-[14px] text-[#4B5563] border-b border-r border-[#EEF2F7] whitespace-nowrap">
                          {patient.patientContactNumber || patient.mobile || "—"}
                        </td>

                        {/* Email */}
                        <td className="px-5 py-4 text-[14px] text-[#4B5563] border-b border-r border-[#EEF2F7] max-w-[200px] truncate">
                          {patient.patientEmail || "—"}
                        </td>

                        {/* Enquiry Date */}
                        <td className="px-5 py-4 border-b border-r border-[#EEF2F7] whitespace-nowrap">
                          <span className="text-[14px] font-medium text-[#374151]">{enquiryDate.date}</span>
                        </td>

                        {/* Status */}
                        <td className="px-5 py-4 border-b border-r border-[#EEF2F7] text-center">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold border ${stageMeta.className}`}>
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${stageMeta.dot}`} />
                            {stageMeta.label}
                          </span>
                        </td>

                        {/* Action */}
                        <td className="px-5 py-4 border-b border-[#EEF2F7] text-center">
                          <div className="flex flex-col items-stretch gap-2 w-[160px] mx-auto">
                            <button
                              type="button"
                              onClick={() => {
                                const selectedCamp = campDetails.find(
                                  (c) => c.campCode === selectedCampCode
                                );
                                
                                
                                navigate(APP_ROUTES.CAMP_PATIENT_DETAIL, {
                                  state: {
                                    enquiryId: patient.id || patient.enquiryId,
                                    patient,
                                    campCode: selectedCampCode,
                                    campName: selectedCamp?.campName || "",
                                    campLocation: selectedCamp?.campLocation || "",
                                    campStartDate: selectedCamp?.campStartDate
                                      ? selectedCamp.campStartDate.split("T")[0]
                                      : "",
                                      campEndDate: selectedCamp?.campEndDate
                                      ? selectedCamp.campEndDate.split("T")[0]
                                      : "", 
                                  },
                                });
                              }}
                              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-[12px] font-semibold border border-[#0A4C8A] text-[#0A4C8A] bg-white hover:bg-[#0A4C8A] hover:text-white transition-all duration-200 whitespace-nowrap"
                            >
                              <Eye size={13} />
                              View Details
                            </button>

                            {stage === PROCEDURE_STAGE.PENDING && (
                              <button
                                type="button"
                                onClick={() => handleConfirmProcedure(patient)}
                                disabled={isUpdatingStatus}
                                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-[12px] font-semibold text-white bg-gradient-to-r from-[#004376] to-[#00BA7E] hover:opacity-90 transition-all duration-200 whitespace-nowrap disabled:opacity-60"
                              >
                                {isUpdatingStatus ? (
                                  <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <CheckCircle2 size={13} />
                                )}
                                Confirm Procedure
                              </button>
                            )}

                            {stage === PROCEDURE_STAGE.CONFIRMED && (
                              <button
                                type="button"
                                onClick={() => handlePatientDischarge(patient)}
                                disabled={isUpdatingStatus}
                                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-[12px] font-semibold text-white bg-gradient-to-r from-[#E58A00] to-[#C77700] hover:opacity-90 transition-all duration-200 whitespace-nowrap disabled:opacity-60"
                              >
                                {isUpdatingStatus ? (
                                  <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <LogOut size={13} />
                                )}
                                Patient Discharge
                              </button>
                            )}

                            {stage === PROCEDURE_STAGE.DISCHARGED &&
                              (isBillingUploaded(patient) ? (
                                <span className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold border border-[#A9E0C1] text-[#2F9D64] bg-[#F0FBF4] whitespace-nowrap">
                                  <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-[#2F9D64]" />
                                  Billing Uploaded
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleCreateBillingService(patient)}
                                  disabled={creatingBillingForId === rowId}
                                  className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-[12px] font-semibold text-white bg-gradient-to-r from-[#004376] to-[#00BA7E] hover:opacity-90 transition-all duration-200 whitespace-nowrap disabled:opacity-60"
                                >
                                  {creatingBillingForId === rowId ? (
                                    <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <FilePlus2 size={13} />
                                  )}
                                  Add Billing
                                </button>
                              ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <BillingUploadSidebar
        isOpen={billingSidebarOpen}
        onClose={closeBillingSidebar}
        onApprovalSuccess={handleBillingApprovalSuccess}
        serviceRecord={billingServiceRecord}
        patientSummary={billingPatientSummary}
        loading={billingLoading}
      />
    </OurLayouts>
  );
};

export default CampPatient;
